/**
 * Background of a calendar item made of its labels' colors, as HARD-EDGED diagonal segments.
 *
 * - no labels      -> `null` (the standard calendar styling stays);
 * - one label      -> a solid fill (`kind: 'solid'`);
 * - several labels -> `linear-gradient(135deg, ...)` from the top-left to the bottom-right
 *   corner, one equal segment per label in the card's own label order; every boundary is a
 *   double color stop at the same position, so there is no blending between neighbours.
 *
 * Deterministic and built only from validated `#rrggbb` colors (anything else is dropped), so
 * it is safe to put in an inline style. Text over the result must not rely on the segments'
 * colors: callers draw it on a backing (`readableText` for a solid fill).
 */
import { isHexColor } from '@/shared/lib/color';

export type LabelBackground =
  { kind: 'solid'; color: string } | { kind: 'segments'; css: string; colors: string[] };

const percent = (value: number) => `${Number(value.toFixed(4))}%`;

export function labelBackground(colors: readonly string[]): LabelBackground | null {
  const valid = colors.filter(isHexColor);
  const first = valid[0];
  if (first === undefined) return null;
  if (valid.length === 1) return { kind: 'solid', color: first };
  const size = 100 / valid.length;
  const stops = valid.map(
    (color, index) => `${color} ${percent(index * size)} ${percent((index + 1) * size)}`,
  );
  return { kind: 'segments', css: `linear-gradient(135deg, ${stops.join(', ')})`, colors: valid };
}
